import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowUpRight, CornerDownLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { searchProducts, SEARCH_SUGGESTIONS } from "@/lib/search";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ResultItem = ({ r, onPick, active, onHover }) => (
  <Link
    to={`/products/${r.p.id}`}
    onClick={onPick}
    onMouseEnter={onHover}
    data-testid={`search-result-${r.p.id}`}
    className={`group flex items-center gap-4 rounded-lg border bg-white p-3 transition-colors ${active ? "border-[#C20500]/50 bg-[#FBEDEA]/50" : "border-slate-200 hover:border-[#C20500]/40"}`}
  >
    <img src={r.p.image} alt={r.p.name} className="h-14 w-14 shrink-0 rounded-md object-contain mix-blend-multiply" />
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold text-slate-900">{r.p.name}</p>
      <p className="truncate text-xs text-slate-500">{r.c.name} · {r.p.tagline}</p>
    </div>
    {active ? <CornerDownLeft className="h-4 w-4 shrink-0 text-[#C20500]" /> : <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-[#C20500]" />}
  </Link>
);

const Suggestions = ({ setQ, testid }) => (
  <div className="mt-3 flex flex-wrap items-center gap-2">
    <span className="text-xs text-slate-500">Popular:</span>
    {SEARCH_SUGGESTIONS.map((s) => (
      <button key={s} type="button" onClick={() => setQ(s)} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-slate-400" data-testid={`${testid}-suggestion-${slug(s)}`}>{s}</button>
    ))}
  </div>
);

const NoResults = ({ onPick, testid }) => (
  <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>
    No matching products. <Link to="/enquiry" onClick={onPick} className="font-semibold text-[#C20500]">Describe your requirement</Link> and our team will source it.
  </p>
);

export const SearchPanel = ({ grid = false, autoFocus = false, onPick, testid = "product-search" }) => {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { results, closest } = searchProducts(q);
  useEffect(() => { setActive(0); }, [q]);
  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    if (e.key === "Enter") { e.preventDefault(); navigate(`/products/${results[active].p.id}`); onPick?.(); }
  };
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Search products"
          placeholder="Type a product, e.g. stretcher, CO2, exit light"
          className="h-14 w-full rounded-xl border border-slate-300 bg-white pl-12 pr-4 text-base outline-none transition-[border-color,box-shadow] focus:border-[#123634] focus:ring-4 focus:ring-[#123634]/10"
          data-testid={`${testid}-input`}
        />
      </div>
      {!q.trim() && <Suggestions setQ={setQ} testid={testid} />}
      {q.trim() && (
        <div className="mt-4">
          <p className="mb-3 text-xs text-slate-500" data-testid={`${testid}-count`}>
            {closest ? `No exact match. Showing ${results.length} closest ${results.length === 1 ? "product" : "products"}` : `${results.length} ${results.length === 1 ? "product" : "products"} found`}
            {results.length > 0 && <span className="hidden sm:inline"> · use ↑ ↓ and Enter</span>}
          </p>
          {results.length ? (
            <div className={grid ? "grid gap-3 sm:grid-cols-2" : "max-h-[55vh] space-y-2 overflow-y-auto pr-1"}>
              {results.map((r, i) => <ResultItem key={r.p.id} r={r} onPick={onPick} active={i === active} onHover={() => setActive(i)} />)}
            </div>
          ) : <NoResults onPick={onPick} testid={testid} />}
        </div>
      )}
    </div>
  );
};

export const SearchButton = ({ mobile = false }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (mobile) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobile]);
  return (
    <>
      {mobile ? (
        <button type="button" onClick={() => setOpen(true)} aria-label="Search products" className="rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100" data-testid="nav-search-button-mobile">
          <Search className="h-5 w-5" />
        </button>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 transition-colors hover:border-slate-300 hover:bg-white" data-testid="nav-search-button">
          <Search className="h-4 w-4" /> Search
          <kbd className="mono hidden whitespace-nowrap rounded border border-slate-200 bg-white px-1.5 text-[10px] text-slate-400 2xl:inline">Ctrl K</kbd>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#F4F6F2] p-5 sm:p-6 [&>*]:min-w-0" data-testid="search-dialog">
          <DialogTitle className="text-lg font-medium" style={{ fontFamily: "Fraunces, serif" }}>Search the catalogue</DialogTitle>
          <SearchPanel autoFocus onPick={() => setOpen(false)} testid="dialog-search" />
        </DialogContent>
      </Dialog>
    </>
  );
};
